import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X, Search, Filter, Calendar, SortAsc, SortDesc } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import DispositifFormContent from './DispositifFormContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPES_ACTIVITE = [
  { value: 'gros_oeuvre', label: 'Gros Œuvre' },
  { value: 'second_oeuvre', label: 'Second Œuvre' },
  { value: 'travaux_publics', label: 'Travaux Publics' },
  { value: 'professionnels_intellectuels', label: 'Professionnels Intellectuels' }
];

export default function GestionDispositifs() {
  const { getAuthHeader } = useAuth();
  const [dispositifs, setDispositifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDispositif, setEditingDispositif] = useState(null);
  
  // États pour les filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'recent', 'old'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    type_activite: 'gros_oeuvre',
    tarif_base: '',
    tarif_par_jour: '',
    tarif_par_chantier: '',
    validite_jours: 360,
    visible_beneficiaire_pro: true,
    visible_beneficiaire_particulier: false,
    visible_cooper: true,
    categorie: '',
    prix_particulier: ''
  });

  useEffect(() => {
    fetchDispositifs();
  }, []);

  const fetchDispositifs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/dispositifs`, { 
        headers: getAuthHeader() 
      });
      // Trier du plus récent au plus ancien
      const sortedDispositifs = (response.data || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setDispositifs(sortedDispositifs);
    } catch {
      toast.error('Erreur lors du chargement des dispositifs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (dispositif = null) => {
    if (dispositif) {
      setEditingDispositif(dispositif);
      setFormData({
        nom: dispositif.nom || '',
        description: dispositif.description || '',
        type_activite: dispositif.type_activite || 'gros_oeuvre',
        tarif_base: dispositif.tarif_base?.toString() || '',
        tarif_par_jour: dispositif.tarif_par_jour?.toString() || '',
        tarif_par_chantier: dispositif.tarif_par_chantier?.toString() || '',
        validite_jours: dispositif.validite_jours || 360,
        visible_beneficiaire_pro: dispositif.visible_beneficiaire_pro ?? true,
        visible_beneficiaire_particulier: dispositif.visible_beneficiaire_particulier ?? false,
        visible_cooper: dispositif.visible_cooper ?? true,
        categorie: dispositif.categorie || '',
        prix_particulier: dispositif.prix_particulier?.toString() || ''
      });
    } else {
      setEditingDispositif(null);
      setFormData({
        nom: '',
        description: '',
        type_activite: 'gros_oeuvre',
        tarif_base: '',
        tarif_par_jour: '',
        tarif_par_chantier: '',
        validite_jours: 360,
        visible_beneficiaire_pro: true,
        visible_beneficiaire_particulier: false,
        visible_cooper: true,
        categorie: '',
        prix_particulier: ''
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingDispositif(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nom || !formData.description || !formData.tarif_base) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const payload = {
        nom: formData.nom,
        description: formData.description,
        type_activite: formData.type_activite,
        tarif_base: parseFloat(formData.tarif_base),
        tarif_par_jour: formData.tarif_par_jour ? parseFloat(formData.tarif_par_jour) : null,
        tarif_par_chantier: formData.tarif_par_chantier ? parseFloat(formData.tarif_par_chantier) : null,
        validite_jours: parseInt(formData.validite_jours),
        visible_beneficiaire_pro: formData.visible_beneficiaire_pro,
        visible_beneficiaire_particulier: formData.visible_beneficiaire_particulier,
        visible_cooper: formData.visible_cooper,
        categorie: formData.categorie || null,
        prix_particulier: formData.prix_particulier ? parseFloat(formData.prix_particulier) : null
      };

      if (editingDispositif) {
        // Mise à jour
        await axios.put(
          `${API}/admin/dispositifs/${editingDispositif.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success('Dispositif mis à jour avec succès');
      } else {
        // Création
        await axios.post(
          `${API}/admin/dispositifs`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success('Dispositif créé avec succès');
      }

      handleCloseDialog();
      fetchDispositifs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleDelete = async (dispositifId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce dispositif ?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/dispositifs/${dispositifId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Dispositif supprimé avec succès');
      fetchDispositifs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Fonction de filtrage et tri
  const getFilteredAndSortedDispositifs = () => {
    let filtered = [...dispositifs];

    // Filtrer par recherche
    if (searchQuery.trim()) {
      filtered = filtered.filter(d => 
        d.nom?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.categorie?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filtrer par date
    const now = new Date();
    const oneMonthAgo = new Date(now.setMonth(now.getMonth() - 1));
    const sixMonthsAgo = new Date(now.setMonth(now.getMonth() - 5));

    if (dateFilter === 'recent') {
      filtered = filtered.filter(d => {
        const createdAt = new Date(d.created_at);
        return createdAt >= oneMonthAgo;
      });
    } else if (dateFilter === 'old') {
      filtered = filtered.filter(d => {
        const createdAt = new Date(d.created_at);
        return createdAt < sixMonthsAgo;
      });
    }

    // Trier par date de création
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  };

  const filteredDispositifs = getFilteredAndSortedDispositifs();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-4 md:py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coppam-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Barre de recherche et filtres */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher par nom, description ou catégorie..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white"
              />
            </div>

            {/* Filtre par date */}
            <div className="flex gap-2">
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-[180px] bg-white">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filtrer par date" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les dates</SelectItem>
                  <SelectItem value="recent">Récent (30 jours)</SelectItem>
                  <SelectItem value="old">Ancien (&gt; 6 mois)</SelectItem>
                </SelectContent>
              </Select>

              {/* Bouton de tri */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                title={sortOrder === 'desc' ? 'Plus récent en premier' : 'Plus ancien en premier'}
              >
                {sortOrder === 'desc' ? <SortDesc className="h-4 w-4" /> : <SortAsc className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Indicateur de résultats */}
          <div className="mt-3 flex items-center justify-between text-sm">
            <p className="text-slate-600">
              <strong>{filteredDispositifs.length}</strong> dispositif(s) trouvé(s)
              {searchQuery && ` pour "${searchQuery}"`}
            </p>
            {(searchQuery || dateFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setDateFilter('all');
                }}
                className="text-blue-600 hover:text-blue-700"
              >
                <X className="h-4 w-4 mr-1" />
                Réinitialiser filtres
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Gestion des Dispositifs</CardTitle>
              <CardDescription>
                Créez et gérez les dispositifs solidaires disponibles sur la plateforme
              </CardDescription>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()} className="bg-coppam-gold hover:bg-coppam-gold/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouveau Dispositif
                </Button>
              </DialogTrigger>
              <DispositifFormContent
                formData={formData}
                setFormData={setFormData}
                editingDispositif={editingDispositif}
                handleSubmit={handleSubmit}
                handleCloseDialog={handleCloseDialog}
                TYPES_ACTIVITE={TYPES_ACTIVITE}
              />
            </Dialog>
          </div>
        </CardHeader>

        <CardContent>
          {dispositifs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 mb-4">Aucun dispositif créé pour le moment</p>
              <Button onClick={() => handleOpenDialog()} className="bg-coppam-gold hover:bg-coppam-gold/90">
                <Plus className="h-4 w-4 mr-2" />
                Créer votre premier dispositif
              </Button>
            </div>
          ) : filteredDispositifs.length === 0 ? (
            <div className="text-center py-12">
              <Filter className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 mb-2">Aucun dispositif ne correspond aux filtres</p>
              <Button 
                variant="ghost" 
                onClick={() => {
                  setSearchQuery('');
                  setDateFilter('all');
                }}
              >
                Réinitialiser les filtres
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredDispositifs.map((dispositif) => (
                <Card key={dispositif.id} className="border border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{dispositif.nom}</h3>
                          <Badge className="bg-blue-600 text-white font-semibold">{dispositif.type_activite}</Badge>
                          {dispositif.categorie && (
                            <Badge className="bg-purple-600 text-white font-semibold">{dispositif.categorie}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mb-3">{dispositif.description}</p>
                        
                        {/* Tarifs */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-xs text-slate-500">Tarif de base</p>
                            <p className="font-semibold text-coppam-blue">{dispositif.tarif_base}€</p>
                          </div>
                          {dispositif.tarif_par_jour && (
                            <div className="bg-slate-50 p-2 rounded">
                              <p className="text-xs text-slate-500">Par jour</p>
                              <p className="font-semibold">{dispositif.tarif_par_jour}€</p>
                            </div>
                          )}
                          {dispositif.tarif_par_chantier && (
                            <div className="bg-slate-50 p-2 rounded">
                              <p className="text-xs text-slate-500">Par chantier</p>
                              <p className="font-semibold">{dispositif.tarif_par_chantier}€</p>
                            </div>
                          )}
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-xs text-slate-500">Validité</p>
                            <p className="font-semibold">{dispositif.validite_jours} jours</p>
                          </div>
                        </div>

                        {/* Visibilité */}
                        <div className="flex flex-wrap gap-2">
                          {dispositif.visible_beneficiaire_pro && (
                            <Badge variant="success" className="text-xs">
                              <Eye className="h-3 w-3 mr-1" />
                              Bénéficiaires Pro
                            </Badge>
                          )}
                          {dispositif.visible_beneficiaire_particulier && (
                            <Badge variant="success" className="text-xs">
                              <Eye className="h-3 w-3 mr-1" />
                              Bénéficiaires Particuliers
                            </Badge>
                          )}
                          {dispositif.visible_cooper && (
                            <Badge variant="success" className="text-xs">
                              <Eye className="h-3 w-3 mr-1" />
                              COOPER'S
                            </Badge>
                          )}
                          {!dispositif.visible_beneficiaire_pro && 
                           !dispositif.visible_beneficiaire_particulier && 
                           !dispositif.visible_cooper && (
                            <Badge className="bg-slate-600 text-white text-xs font-semibold">
                              <EyeOff className="h-3 w-3 mr-1" />
                              Non visible
                            </Badge>
                          )}
                        </div>

                        {/* Date de création et statistiques */}
                        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                          {dispositif.created_at && (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Créé le {new Date(dispositif.created_at).toLocaleDateString('fr-FR')}
                            </div>
                          )}
                          {dispositif.statistiques && (
                            <div>
                              📊 {dispositif.statistiques.souscriptions_actives} souscription(s) active(s)
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenDialog(dispositif)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(dispositif.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
